<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-clock-o animated bounceInDown show-info"></i>
						Attendance Shifts
					</h3>
					<p class="text-muted" style="margin-left:4px">
						Working patterns. An overnight shift is credited to a single date, so it does not read
						as two short days.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add shift
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Shift</th>
							<th>Timing</th>
							<th>Break</th>
							<th>Grace</th>
							<th>Full / half day</th>
							<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="6" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="6" class="text-center">
									No shifts yet. Policies set to "no shift" work without one.
								</td>
							</tr>
							<tr v-for="row in rows" :key="row.id">
								<td>
									<strong>{{ row.name }}</strong>
									<br />
									<small class="text-muted">{{ row.code }}</small>
								</td>
								<td>
									{{ row.start_time.slice(0, 5) }} – {{ row.end_time.slice(0, 5) }}
									<span v-if="row.crosses_midnight" class="badge bg-warning text-dark">Overnight</span>
								</td>
								<td>{{ row.break_minutes }} min</td>
								<td>{{ row.grace_in_min }} / {{ row.grace_out_min }} min</td>
								<td>{{ Number(row.min_hours_full) }}h / {{ Number(row.min_hours_half) }}h</td>
								<td style="white-space:nowrap">
									<button class="btn btn-sm btn-primary" @click="edit(row)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(row)" title="Deactivate">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="attendance-shift" :width="560" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">{{ form.id ? 'Edit shift' : 'Add shift' }}</h4>
				<div class="row">
					<div class="col-md-6 form-group">
						<label>Code</label>
						<input type="text" class="form-control form-cascade-control" placeholder="GEN" v-model="form.code" />
					</div>
					<div class="col-md-6 form-group">
						<label>Name</label>
						<input type="text" class="form-control form-cascade-control" placeholder="General 9-6" v-model="form.name" />
					</div>
				</div>
				<div class="row">
					<div class="col-md-6 form-group">
						<label>Start</label>
						<input type="time" class="form-control form-cascade-control" v-model="form.start_time" />
					</div>
					<div class="col-md-6 form-group">
						<label>End</label>
						<input type="time" class="form-control form-cascade-control" v-model="form.end_time" />
					</div>
				</div>

				<div class="alert alert-info" v-if="crossesMidnight">
					This shift runs past midnight. Choose which date it belongs to below.
				</div>
				<div class="form-group" v-if="crossesMidnight">
					<label>Credit the shift to</label>
					<select class="form-control form-cascade-control" v-model="form.day_attribution">
						<option value="start_day">The day it starts</option>
						<option value="end_day">The day it ends</option>
					</select>
				</div>

				<div class="row">
					<div class="col-md-4 form-group">
						<label>Break (min)</label>
						<input type="number" min="0" max="480" class="form-control form-cascade-control" v-model.number="form.break_minutes" />
					</div>
					<div class="col-md-4 form-group">
						<label>Grace in (min)</label>
						<input type="number" min="0" max="240" class="form-control form-cascade-control" v-model.number="form.grace_in_min" />
					</div>
					<div class="col-md-4 form-group">
						<label>Grace out (min)</label>
						<input type="number" min="0" max="240" class="form-control form-cascade-control" v-model.number="form.grace_out_min" />
					</div>
				</div>
				<div class="row">
					<div class="col-md-6 form-group">
						<label>Full day (hours)</label>
						<input type="number" min="0" max="24" step="0.01" class="form-control form-cascade-control" v-model.number="form.min_hours_full" />
					</div>
					<div class="col-md-6 form-group">
						<label>Half day (hours)</label>
						<input type="number" min="0" max="24" step="0.01" class="form-control form-cascade-control" v-model.number="form.min_hours_half" />
					</div>
				</div>

				<p class="text-muted" v-if="netMinutes !== null">
					Net working time after the break: {{ (netMinutes / 60).toFixed(2) }}h
				</p>
				<div class="alert alert-warning" v-if="thresholdWarning">{{ thresholdWarning }}</div>

				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-shift')">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">Save</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { shiftService, errorMessage } from './attendanceService'

export default {
	name: 'AttendanceShifts',
	data() {
		return {
			rows: [],
			loading: false,
			error: '',
			saving: false,
			form: this.blank(),
		}
	},
	computed: {
		/** Minutes actually available once the unpaid break is removed. */
		netMinutes() {
			const s = String(this.form.start_time || '').split(':').map(Number)
			const e = String(this.form.end_time || '').split(':').map(Number)
			if ([s[0], s[1], e[0], e[1]].some((n) => Number.isNaN(n))) return null
			let span = e[0] * 60 + e[1] - (s[0] * 60 + s[1])
			if (span <= 0) span += 24 * 60
			return span - (this.form.break_minutes || 0)
		},
		crossesMidnight() {
			const s = String(this.form.start_time || '').split(':').map(Number)
			const e = String(this.form.end_time || '').split(':').map(Number)
			if ([s[0], s[1], e[0], e[1]].some((n) => Number.isNaN(n))) return false
			return e[0] * 60 + e[1] <= s[0] * 60 + s[1]
		},
		/**
		 * A full-day bar above the shift's own net hours can never be reached, so
		 * every day would score as a half day. Worth catching before it is saved.
		 */
		thresholdWarning() {
			if (this.netMinutes === null) return null
			const required = Math.round((this.form.min_hours_full || 0) * 60)
			if (required > this.netMinutes) {
				return `This shift only provides ${(this.netMinutes / 60).toFixed(
					2
				)}h after the break, so a full day of ${
					this.form.min_hours_full
				}h can never be reached. Lower the full-day hours or shorten the break.`
			}
			return null
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		blank() {
			return {
				id: null,
				code: '',
				name: '',
				start_time: '09:00',
				end_time: '18:00',
				day_attribution: 'start_day',
				break_minutes: 60,
				grace_in_min: 10,
				grace_out_min: 10,
				min_hours_full: 8,
				min_hours_half: 4,
			}
		},
		refresh() {
			this.loading = true
			this.error = ''
			shiftService
				.list()
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load shifts.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		create() {
			this.form = this.blank()
			this.$modal.show('attendance-shift')
		},
		edit(shift) {
			this.form = {
				...shift,
				start_time: shift.start_time.slice(0, 5),
				end_time: shift.end_time.slice(0, 5),
				min_hours_full: Number(shift.min_hours_full),
				min_hours_half: Number(shift.min_hours_half),
			}
			this.$modal.show('attendance-shift')
		},
		save() {
			this.saving = true
			const body = { ...this.form }
			const id = body.id
			delete body.id
			const req = id ? shiftService.update(id, body) : shiftService.create(body)
			req
				.then(() => {
					this.$notify({ text: 'Saved', type: 'success' })
					this.$modal.hide('attendance-shift')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		remove(shift) {
			if (!window.confirm(`Deactivate "${shift.name}"?`)) return
			shiftService
				.remove(shift.id)
				.then(() => {
					this.$notify({ text: 'Deactivated', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
</style>
